// ============================================================================
// Contracts — list page. Role-aware default filters (per Phase 1 spec):
//   Sales / AM:     created_by_me, all non-final statuses
//   Finance (Jose): status = Pending_Finance
//   Executive:      status IN (Pending_Legal, Pending_Executive)
//   Superuser:      status NOT IN (Signed, Active, Expired, Cancelled)
// Status pills always shown so users can override the default.
// ============================================================================

import { canStartContract, hasFinanceCapability, isAccountManagerSeat, isExecutiveSeat, isFinanceSeat, isSalesSeat, isSuperuser } from '../../shared/role-gates';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { useAuth } from '../../shared/auth';

import {
  Button,
  EmptyState,
  ErrorState,
  PageHeader,
  Skeleton,
} from '../../shared/components';
import { ContractCard } from './contract-card';
import { useContracts } from './hooks/use-contract-data';
import {
  CONTRACT_STATUSES,
  CONTRACT_STATUS_LABELS,
  type ContractStatus,
} from './types';

const ALL_NON_FINAL: ContractStatus[] = [
  'Draft',
  'Pending_Finance',
  'Pending_Legal',
  'Pending_Executive',
  'Ready_for_Signature',
  'Sent_for_Signature',
];

const FINANCE_DEFAULT: ContractStatus[] = ['Pending_Finance'];
const EXEC_DEFAULT: ContractStatus[] = ['Pending_Legal', 'Pending_Executive'];

function defaultStatusesForRole(
  role: string | null | undefined,
  financeCapable: boolean,
): ContractStatus[] {
  if (isFinanceSeat(role)) return FINANCE_DEFAULT;
  if (isExecutiveSeat(role)) return EXEC_DEFAULT;
  if (isSuperuser(role)) return ALL_NON_FINAL;
  // extra_roles 'finance' hat holders (e.g. Antonela, primary hr) work the
  // Pending_Finance queue like finance.
  if (financeCapable) return FINANCE_DEFAULT;
  return ALL_NON_FINAL;
}

function scopeDefaultForRole(role: string | null | undefined): 'mine' | 'all' {
  // Sales + AMs default to mine; everyone else sees all.
  if (isSalesSeat(role) || isAccountManagerSeat(role)) return 'mine';
  return 'all';
}

function canCreateContract(role: string | null | undefined): boolean {
  return canStartContract(role);
}

export default function ContractList() {
  const { employee, profile } = useAuth();
  const role = profile?.role ?? null;
  const financeCapable = hasFinanceCapability(profile);

  const [scope, setScope] = useState<'mine' | 'all'>(() =>
    scopeDefaultForRole(role),
  );
  const [statuses, setStatuses] = useState<ContractStatus[]>(() =>
    defaultStatusesForRole(role, financeCapable),
  );

  // Re-seed defaults if the role loads after first render.
  useEffect(() => {
    setStatuses(defaultStatusesForRole(role, financeCapable));
    setScope(scopeDefaultForRole(role));
  }, [role, financeCapable]);

  const filter = useMemo(
    () => ({
      createdBy:
        scope === 'mine' && employee?.employee_id
          ? employee.employee_id
          : undefined,
      statuses: statuses.length > 0 ? statuses : undefined,
    }),
    [scope, employee?.employee_id, statuses],
  );

  const { data, isLoading, error, refetch } = useContracts(filter);

  function toggleStatus(s: ContractStatus) {
    setStatuses((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s],
    );
  }

  // Build a {status: count} from the unfiltered list-ish — we just count within
  // the current filtered set since RLS already scopes; small enough to be cheap.
  const countsByStatus = useMemo(() => {
    const counts: Partial<Record<ContractStatus, number>> = {};
    for (const row of data) {
      counts[row.status] = (counts[row.status] ?? 0) + 1;
    }
    return counts;
  }, [data]);

  return (
    <div className="mx-auto max-w-4xl px-4 py-6">
      <PageHeader
        title="Contracts"
        meta="Customer contracts — from approved bid through signature."
        actions={
          canCreateContract(role) ? (
            <Link to="/contracts/new">
              <Button variant="primary" size="sm">
                <Plus className="h-4 w-4" /> New contract
              </Button>
            </Link>
          ) : null
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2 text-sm">
        <div className="flex items-center gap-1 rounded-md border border-tcs-border bg-tcs-bg p-1">
          {(['mine', 'all'] as const).map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => setScope(opt)}
              className={`rounded px-3 py-1 text-xs font-medium uppercase tracking-wide ${
                scope === opt
                  ? 'bg-tcs-inverse text-tcs-inverse-ink'
                  : 'text-tcs-text-secondary hover:text-tcs-text'
              }`}
            >
              {opt}
            </button>
          ))}
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-1 text-sm">
        <span className="mr-1 text-[11px] uppercase tracking-wide text-tcs-text-muted">
          Status:
        </span>
        {CONTRACT_STATUSES.map((s) => {
          const active = statuses.includes(s);
          const count = countsByStatus[s] ?? 0;
          return (
            <button
              key={s}
              type="button"
              onClick={() => toggleStatus(s)}
              className={`rounded-md border px-2.5 py-1 text-xs font-medium ${
                active
                  ? 'border-tcs-inverse bg-tcs-inverse text-tcs-inverse-ink'
                  : 'border-tcs-border bg-tcs-bg text-tcs-text-secondary hover:border-tcs-inverse/40'
              }`}
            >
              {CONTRACT_STATUS_LABELS[s]}
              {count > 0 && (
                <span className="ml-1 opacity-80">({count})</span>
              )}
            </button>
          );
        })}
      </div>

      {error && (
        <ErrorState
          title="Couldn't load contracts"
          body={error}
          onRetry={refetch}
        />
      )}

      {isLoading && !error && (
        <ul className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <li key={i}>
              <Skeleton className="h-20 w-full rounded-md" />
            </li>
          ))}
        </ul>
      )}

      {!isLoading && !error && data.length === 0 && (
        <EmptyState
          title="No contracts"
          body={
            scope === 'mine'
              ? "You haven't created any contracts that match the current filters."
              : 'No contracts match the current filters.'
          }
        />
      )}

      {!isLoading && !error && data.length > 0 && (
        <ul className="space-y-2">
          {data.map((row) => (
            <ContractCard key={row.contract_id} row={row} />
          ))}
        </ul>
      )}
    </div>
  );
}
